import React, { useState, useEffect, useContext} from 'react';
import { Box,
        TextField,
        Typography,
        Button,
        Avatar,
        Stack,
        Select,
        MenuItem,
        Table,
        TableBody,
        TableCell,
        TableRow,
        Unstable_Grid2 as Grid,
        Card,
        CardContent,
        FormControl,
        InputLabel,
        OutlinedInput,
        Checkbox,
        ListItemText,
        Chip
    } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AuthContext from '../../contexts/AuthContext';
import { setSnackDetails, setMetaData, setSweetAlert } from '../../service/actions/common';
import axios from 'axios';
import { setContractorData } from '../../service/actions/index';

import { useFormik } from 'formik';
import * as yup from 'yup';

const regMatch = /^((http|https):\/\/)?(www\.)?(?!.*(http|https|www\.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+(\/)?([\w?[a-zA-Z-_%\/@?]+)*([^\/\w?[a-zA-Z0-9_-]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/;
const validationSchema = yup.object({
    contractor_name: yup
        .string('Contractor Name')
        .required('Contractor Name is required'),
    company_name: yup
        .string('Company Name')
        .required('Company Name is required'),
    email: yup
        .string('Email')
        .matches(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'Enter a valid email')
        .required('Email is required'),
    phone: yup
        .string('Phone Number')
        .matches(/^[3-9]\d{9}$/, {message: "Please enter valid phone number.", excludeEmptyString: false})
        .required('Phone Number is required'),
    category_ids: yup
        .array()
        .min(1, 'Category is required'),
    website: yup
        .string('Website')
        .nullable()
        .matches(regMatch, "Website should be a valid URL")
    });

const ContractorProfile = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    let { contractorId } = useParams();
    const { token, user } = useContext(AuthContext);
    const formatOptions = JSON.parse(process.env.REACT_APP_DATE_TIME_FORMAT || '{}');
    const [formated_created_at, setCreatedAt] = useState('');
    const [belongs_to, setBelongsTo] = useState('');
    const [isEdit, setIsEdit] = useState(false);
    const profile = useSelector((state) => state.contractorData);
    const [preview, setPreview] = useState('');
    const [cities, setCities] = useState([]);
    const [states, setStates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryDict, setCategoryDict] = useState([]);
    let metaData = useSelector((state) => state.metaData);

    const catchError = async (error) => {
        if (error.response) {
            if (error.response.status === 401) {
                navigate('/sign-in');
            } else if (error.response.status >= 500) {
                dispatch(setSweetAlert({ open: true, title: 'Server Error', text: '' }));
            } else if (error.response.status === 400) {
                dispatch(setSnackDetails({openState : true, message : error.response.data.message, severity : 'error'}));
                return false;
            } else {
                console.log(error);
                dispatch(setSweetAlert({ open: true, title: 'Error', text: '' }));
            }
        } else if (error.request) {
            dispatch(setSweetAlert({ open: true, title: 'Connection Lost', text: '' }));
        } else {
            console.log(error);
            dispatch(setSweetAlert({ open: true, title: 'Error', text: 'An unknown error occurred.' }));
        }
    };
    const fetchContractor = async () => {
        if (user && token) {
            try {
                let uri = `/contractor/${contractorId}`;
                const response = await axios.get(uri, {
                    headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    },
                });
                const contractor_user = response.data.contractor;
                dispatch(setContractorData(contractor_user));
                setBelongsTo(contractor_user.customer ? contractor_user.customer.email : '');
                const created_at = Date.parse(contractor_user.createdAt);
                const temp_created_at =  new Intl.DateTimeFormat('en-US', formatOptions).format(created_at);
                setCreatedAt(temp_created_at);
                metaData  = {
                    title: `Preferred Contractor | ${contractor_user?.email}`,
                    description: `Preferred Contractor | ${contractor_user?.email}`
                };
                dispatch(setMetaData(metaData));
            } catch (error) {
                catchError(error);
            }
        }
    };
    const fetchStates = async () => {
        try {
                const response = await axios.get(`/states`);
                setStates(response.data);
            } catch (error) {
                catchError(error);
            }
    };
    const fetchCategories = async () => {
        try {
            const response = await axios.get(`/categories`);
            setCategories(response.data);
            } catch (error) {
                catchError(error);
            }
    };
    const fetchCities = async () => {
        let state_id = formik.values.state ? formik.values.state : profile.state;
        if ( state_id ) {
            try {
                const response = await axios.post(`/cities`, {
                    state_id : state_id
                });
                setCities(response.data);
            } catch (error) {
                catchError(error);
            }
        }
    };

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
            },
        },
    };
    const formik = useFormik({
        initialValues: {
            customer: profile?.customer,
            contractor_id: profile?._id,
            contractor_name: profile?.contractor_name,
            company_name: profile?.company_name,
            email: profile?.email,
            phone: profile?.phone,
            address_1: profile?.address_1,
            address_2: profile?.address_2,
            category_ids: (categories.length > 0 ) ? profile.category_ids : [],
            state: (states.length > 0 ) ? profile.state_id : "",
            city: (cities.length > 0 ) ? profile.city_id : "",
            zip_code: profile?.zip_code,
            website: profile?.website,
            status: profile?.status
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                if (user && token ) {
                    const response = await axios.post('/contractor/update-profile', values, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                    });
                    if (response.status === 200) {
                        dispatch(setContractorData(response.data.contractor));
                        dispatch(setSnackDetails({openState : true, message : response.data.message, severity : 'success'}));
                        setIsEdit(false);
                    } else {
                        dispatch(setSnackDetails({openState : true, message : response.data.message, severity : 'error'}));
                    }
                } else {
                    console.log('unauthorized');
                }
            } catch (error) {
                catchError(error);
            }
        },
    });

    const handleProfilePictureChange = async (event) => {
        console.log('called handle change');
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const formData = new FormData();
            formData.append('profilePhoto', file);
            setPreview(URL.createObjectURL(file));
            formData.append('contractor_id', contractorId);
            try {
                if (user && token ) {
                    const response = await axios.post('/contractor/profile-photo', formData, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            "Content-type": "multipart/form-data",
                        },
                    });
                    dispatch(setContractorData(response.data.contractor));
                    dispatch(setSnackDetails({openState : true, message : response.data.message, severity : 'success'}));
                } else {
                    console.log('unauthorized');
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 401) {
                        navigate('/sign-in');
                    } else if (error.response.status >= 500) {
                        dispatch(setSweetAlert({ open: true, title: 'Server Error', text: '' }));
                    } else {
                        dispatch(setSweetAlert({ open: true, title: 'Error', text: '' }));
                    }
                } else if (error.request) {
                    dispatch(setSweetAlert({ open: true, title: 'Connection Lost', text: '' }));
                } else {
                    dispatch(setSweetAlert({ open: true, title: 'Error', text: 'An unknown error occurred.' }));
                }
            }
        }
    };

    useEffect(() => {
        fetchCategories();
        fetchStates();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        fetchContractor();
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        formik.values.city = '';
        fetchCities();
        // eslint-disable-next-line
    }, [formik.values.state]);
    useEffect(() => {
        const categoryAsso = categories.reduce((acc, category) => {
            acc[category._id] = category.category_name;
            return acc;
          }, {});
          setCategoryDict(categoryAsso);
      }, [categories]);

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                <Button component={Link} to="/contractor-users">
                    <ArrowBackIcon />
                </Button>
                <Typography variant="h4">{profile?.company_name}</Typography>

                { isEdit ? (
                    <Button color="primary" variant="contained" onClick={formik.handleSubmit} sx={{ borderRadius: 28, ml: "auto" }} >
                        Save
                    </Button>
                ) : (
                    <Button color="primary" variant="outlined" onClick={(e) => {setIsEdit(true)}} sx={{ borderRadius: 28, ml: "auto" }} >
                        Edit
                    </Button>
                )}
            </Stack>
            <Box
                sx={{
                    margin: 'auto',
                    padding: 2,
                    border: '1px solid #ddd',
                    borderRadius: 2,
                    boxShadow: 1,
                    mt:2,
                    backgroundColor:'#FFF'
                }}
            >
                <Typography variant="h4" gutterBottom>
                    About
                </Typography>
                <Grid container spacing={2}>
                <Grid xs={12} md={8}>
                    <Card sx={{
                        backgroundColor:'#F9FAFB',
                    }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Profile
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Contractor Name"
                                        name="contractor_name"
                                        id="contractor_name"
                                        type="text"
                                        required
                                        disabled={!isEdit}
                                        inputlabelprops={{ shrink: true }}
                                        autoFocus
                                        value={formik.values.contractor_name || ''}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.contractor_name && Boolean(formik.errors.contractor_name)}
                                        helperText={formik.touched.contractor_name && formik.errors.contractor_name}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Company Name"
                                        name="company_name"
                                        id="company_name"
                                        type="text"
                                        required
                                        disabled={!isEdit}
                                        inputlabelprops={{ shrink: true }}
                                        value={formik.values.company_name || ''}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.company_name && Boolean(formik.errors.company_name)}
                                        helperText={formik.touched.company_name && formik.errors.company_name}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <TextField
                                        label="Email"
                                        name="email"
                                        id="email"
                                        type="text"
                                        required
                                        disabled={!isEdit}
                                        inputlabelprops={{ shrink: true }}
                                        fullWidth
                                        value={formik.values.email || ''}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                        helperText={formik.touched.email && formik.errors.email}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <TextField
                                        label="Phone Number"
                                        name="phone"
                                        id="phone"
                                        type="text"
                                        required
                                        fullWidth
                                        disabled={!isEdit}
                                        inputlabelprops={{ shrink: true }}
                                        value={formik.values.phone || ''}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.phone && Boolean(formik.errors.phone)}
                                        helperText={formik.touched.phone && formik.errors.phone}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <TextField
                                        label="Address Line 1"
                                        name="address_1"
                                        id="address_1"
                                        type="text"
                                        fullWidth
                                        disabled={!isEdit}
                                        inputlabelprops={{ shrink: true }}
                                        value={formik.values.address_1 || ''}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.address_1 && Boolean(formik.errors.address_1)}
                                        helperText={formik.touched.address_1 && formik.errors.address_1}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <TextField
                                        label="Address Line 2"
                                        name="address_2"
                                        id="address_2"
                                        type="text"
                                        fullWidth
                                        disabled={!isEdit}
                                        inputlabelprops={{ shrink: true }}
                                        value={formik.values.address_2 || ''}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.address_2 && Boolean(formik.errors.address_2)}
                                        helperText={formik.touched.address_2 && formik.errors.address_2}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <FormControl
                                            fullWidth
                                            variant="outlined"
                                            error={formik.touched.state && Boolean(formik.errors.state)}
                                        >
                                        <InputLabel id="state-label">State</InputLabel>
                                        <Select
                                            id="state"
                                            name="state"
                                            label="State"
                                            labelId="state-label"
                                            disabled={!isEdit}
                                            fullWidth
                                            inputlabelprops={{ shrink: true }}
                                            value={formik.values.state || ''}
                                            onChange={formik.handleChange}
                                            sx={{ mr: 1 }}
                                        >
                                            {states.map((statt) => (
                                            <MenuItem key={statt._id} value={statt._id}>
                                                {statt.name}
                                            </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid xs={12}>
                                    <FormControl
                                            fullWidth
                                            variant="outlined"
                                            error={formik.touched.city && Boolean(formik.errors.city)}
                                        >
                                        <InputLabel id="city-label">City</InputLabel>
                                        <Select
                                            id="city"
                                            name="city"
                                            label="City"
                                            labelId="city-label"
                                            disabled={!isEdit}
                                            fullWidth
                                            inputlabelprops={{ shrink: true }}
                                            value={formik.values.city || ''}
                                            onChange={formik.handleChange}
                                            sx={{ mr: 1 }}
                                        >
                                            {cities.map((citi) => (
                                            <MenuItem key={citi._id} value={citi._id}>
                                                {citi.name}
                                            </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid xs={12}>
                                    <TextField
                                        label="Zip Code"
                                        name="zip_code"
                                        id="zip_code"
                                        type="text"
                                        fullWidth
                                        disabled={!isEdit}
                                        inputlabelprops={{ shrink: true }}
                                        value={formik.values.zip_code || ''}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.zip_code && Boolean(formik.errors.zip_code)}
                                        helperText={formik.touched.zip_code && formik.errors.zip_code}
                                    />
                                </Grid>
                                <Grid xs={12}>
                                    <TextField
                                        label="Website (optional)"
                                        name="website"
                                        id="website"
                                        type="text"
                                        fullWidth
                                        disabled={!isEdit}
                                        inputlabelprops={{ shrink: true }}
                                        value={formik.values.website || ''}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.website && Boolean(formik.errors.website)}
                                        helperText={formik.touched.website && formik.errors.website}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid xs={12} md={4}>
                <Card>
                    <CardContent>
                        <Typography variant="h6">Profile Image</Typography>
                        <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" mb={1}>
                            <Avatar
                                alt={profile.company_name}
                                src={ profile.profilePhoto || preview}
                                sx={{ width: 150, height: 150 }}
                            />
                            <Typography variant="caption">File Types: .jpg, .jpeg, .png</Typography>
                            <Typography variant="caption">File Size: 5MB</Typography>

                            { isEdit ? (
                            <Button type="button" component="label" variant="contained" fullWidth sx={{ m: 2  }}>
                                Replace Image
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleProfilePictureChange}
                                />
                            </Button>
                            ) : (
                                ''
                            )}
                        </Box>
                    </CardContent>
                </Card>
                <Box mt={2} display="flex" justifyContent="space-between">
                    <FormControl
                            fullWidth
                            variant="outlined"
                            error={formik.touched.status && Boolean(formik.errors.status)}
                        >
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select
                            labelId="status-label"
                            id="status"
                            name="status"
                            label="Status"
                            disabled={!isEdit}
                            inputlabelprops={{ shrink: true }}
                            value={formik.values.status || 0}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        >
                            <MenuItem key={1} value={1}>Active</MenuItem>
                            <MenuItem key={0} value={0}>Inactive</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box mt={2} display="flex" justifyContent="space-between">
                    <FormControl
                            fullWidth
                            variant="outlined"
                            error={formik.touched.category_ids && Boolean(formik.errors.category_ids)}
                        >
                        <InputLabel id="category-label">Category</InputLabel>
                        <Select
                            id="category_ids"
                            name="category_ids"
                            labelId="category-label"
                            disabled={!isEdit}
                            multiple
                            value={formik.values.category_ids || []}
                            onChange={formik.handleChange}
                            input={<OutlinedInput label="Tag" />}
                            inputlabelprops={{ shrink: true }}
                            onBlur={formik.handleBlur}
                            MenuProps={MenuProps}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map((id) => (
                                    <Chip
                                        key={id}
                                        label={categoryDict[id]}
                                        color="primary"
                                        variant="filled"
                                        sx={{
                                            WebkitTextFillColor: 'white',
                                            opacity: 1
                                        }}
                                    />
                                  ))}
                                </Box>
                              )}
                            >
                            {categories.map((categori) => (
                                <MenuItem key={categori._id} value={categori._id}>
                                <Checkbox
                                    checked={ formik.values.category_ids?.includes(categori._id)}
                                    />
                                <ListItemText primary={categori.category_name} />
                                </MenuItem>
                            ))}
                            </Select>
                    </FormControl>
                </Box>
                </Grid>
            </Grid>
            </Box>

            <Box
                sx={{
                    margin: 'auto',
                    padding: 2,
                    border: '1px solid #ddd',
                    borderRadius: 2,
                    boxShadow: 1,
                    mt:2,
                    backgroundColor:'#FFF'
                }}
            >
                <Typography variant="h5" gutterBottom>Stats</Typography>
                <Table sx={{ width: '50%' }} aria-label="simple table">
                    <TableBody>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell sx={{ fontWeight: 'bold' }} align="left">Belongs To</TableCell>
                            <TableCell align="left" sx={{ color: 'primary.main' }}>{belongs_to}</TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell sx={{ fontWeight: 'bold' }} align="left">Date Added</TableCell>
                            <TableCell align="left" sx={{ color: 'primary.main' }}>{formated_created_at}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Box>
        </Box>
    );
};

export default ContractorProfile;